import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Collapse,
  IconButton
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';

// Рекурсивный компонент для отображения узлов дерева
function TreeNode({ node, path = 'ontology', level = 0, onSelect, selectedPath }) {
  const [open, setOpen] = useState(level < 2); // Автоматически раскрываем первые 2 уровня

  const handleClick = () => {
    if (hasChildren(node)) {
      setOpen(!open);
    }
    onSelect(path, node);
  };

  const hasChildren = (data) => {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === 'object') {
      // Исключаем системные поля из отображения в дереве
      const systemFields = ['name', 'type', 'description', 'file_path', 'folder', 'object_count', 'source', 'storage'];
      const objectKeys = Object.keys(data).filter(key => !systemFields.includes(key));
      return objectKeys.length > 0 || (Array.isArray(data.items) && data.items.length > 0);
    }
    return false;
  };

  const getNodeLabel = (data, currentPath) => {
    if (typeof data === 'object') {
      if (data.name) return data.name;
      if (data.description) return data.description;
      // Для ключей объектов возвращаем последнюю часть пути
      const pathParts = currentPath.split('.');
      return pathParts[pathParts.length - 1];
    }
    return String(data);
  };

  const getNodeIcon = (data) => {
    if (Array.isArray(data)) {
      return <DescriptionIcon color="primary" />;
    }
    if (typeof data === 'object') {
      if (data.name && data.description) {
        return <DescriptionIcon color="secondary" />;
      }
      return <FolderIcon color="action" />;
    }
    return <CategoryIcon color="disabled" />;
  };

  const renderChildren = () => {
    if (!hasChildren(node)) return null;

    let children = [];
    
    if (Array.isArray(node)) {
      children = node.map((item, index) => (
        <TreeNode
          key={index}
          node={item}
          path={`${path}[${index}]`}
          level={level + 1}
          onSelect={onSelect}
          selectedPath={selectedPath}
        />
      ));
    } else if (typeof node === 'object') {
      // Фильтруем системные поля
      const systemFields = ['name', 'type', 'description', 'file_path', 'folder', 'object_count', 'source', 'storage'];
      const objectKeys = Object.keys(node).filter(key => !systemFields.includes(key));
      
      children = objectKeys.map(key => {
        const childData = node[key];
        // Пропускаем пустые значения
        if (!childData || (Array.isArray(childData) && childData.length === 0)) {
          return null;
        }
        return (
          <TreeNode
            key={key}
            node={childData}
            path={`${path}.${key}`}
            level={level + 1}
            onSelect={onSelect}
            selectedPath={selectedPath}
          />
        );
      }).filter(Boolean);

      // Если есть items (для коллекций), добавляем их
      if (Array.isArray(node.items) && node.items.length > 0) {
        children = [
          ...children,
          ...node.items.map((item, index) => (
            <TreeNode
              key={`items[${index}]`}
              node={item}
              path={`${path}.items[${index}]`}
              level={level + 1}
              onSelect={onSelect}
              selectedPath={selectedPath}
            />
          ))
        ];
      }
    }

    return children;
  };

  const label = getNodeLabel(node, path);
  const hasChildrenNodes = hasChildren(node);

  return (
    <>
      <ListItemButton 
        sx={{ 
          pl: 2 + level * 3,
          backgroundColor: selectedPath === path ? 'action.selected' : 'transparent'
        }}
        onClick={handleClick}
        selected={selectedPath === path}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {getNodeIcon(node)}
        </ListItemIcon>
        <ListItemText 
          primary={
            <Typography variant="body2" noWrap>
              {label}
            </Typography>
          }
          secondary={
            typeof node === 'object' && node.description ? (
              <Typography variant="caption" color="text.secondary">
                {node.description}
              </Typography>
            ) : Array.isArray(node) ? (
              <Typography variant="caption" color="text.secondary">
                {node.length} элементов
              </Typography>
            ) : null
          }
        />
        {hasChildrenNodes && (
          <IconButton size="small" onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </ListItemButton>
      {hasChildrenNodes && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding dense>
            {renderChildren()}
          </List>
        </Collapse>
      )}
    </>
  );
}

export default function CustomTreeView({ data, onNodeSelect, selectedPath, height = "400px" }) {
  const handleNodeSelect = (path, node) => {
    onNodeSelect(path, node);
  };

  if (!data) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Нет данных для отображения
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height, 
      overflow: 'auto',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1
    }}>
      <List dense>
        <TreeNode
          node={data}
          path="ontology"
          onSelect={handleNodeSelect}
          selectedPath={selectedPath}
        />
      </List>
    </Box>
  );
}